import React, { useCallback, useMemo, useRef } from 'react'
import cn from 'clsx'
import { rem } from 'polished'
import styled from 'styled-components'
import Pane from 'components/Common/Pane'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import Divider from 'components/Luxkit/Divider'
import Clickable from 'components/Common/Clickable'
import Switch from 'components/Luxkit/Switch/Switch'
import Caption from 'components/Luxkit/Typography/Caption'
import ClickableListItem from 'components/Luxkit/List/ClickableListItem'
import Heading from 'components/Luxkit/Typography/Heading'
import BodyText from 'components/Luxkit/Typography/BodyText'
import FormatCurrency from 'components/Common/FormatCurrency'
import LineDoorIcon from 'components/Luxkit/Icons/line/LineDoorIcon'
import LinePlaneDepartureIcon from 'components/Luxkit/Icons/line/LinePlaneDepartureIcon'
import { pluralizeToString } from 'lib/string/pluralize'
import interactiveFocusStyle from 'styles/utils/interactiveFocusStyle'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { MobileMultiRoomMode, OfferPageActions, OfferPageFilterType } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getActivePackagesByRoom, getHasPackageChangedFromSelection, getMultiRoomPackageTotals, MultiRoomPackageTotals, PackageOptionViewByRoom } from 'selectors/offerPage/multipleRoomBookingSelectors'
import MobileMultipleRoomsIndividualRoomsContent from './MobileMultipleRoomsIndividualRoomsContent'
import MobileMultipleRoomsSameRoomContent from './MobileMultipleRoomsSameRoomContent'
import MessageBanner from 'components/Luxkit/Banners/MessageBanner'
import { showSnackbar } from 'components/Luxkit/Snackbar/AppSnackbar'

const HeaderSection = styled(Pane)`
  padding: ${rem(20)} ${rem(20)} ${rem(16)};
`

const TogglesSection = styled(Pane)`
  padding: ${rem(4)} 0;
`

const DividerContainer = styled.div`
  padding: 0 ${rem(16)};
`

const ToggleButtonsContainer = styled(Group)`
  padding: ${rem(8)};
  background-color: ${({ theme }) => theme.palette.neutral.default.eight};
  border-radius: ${props => props.theme.borderRadius.round};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
`

const ToggleButton = styled(Clickable)`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  height: ${rem(44)};
  padding: ${rem(4)} ${rem(16)};
  border: 1px solid transparent;
  border-radius: ${props => props.theme.borderRadius.round};
  background-color: ${({ theme }) => theme.palette.neutral.default.eight};
  transition: background-color 0.2s, box-shadow 0.2s, color 0.2s, border-color 0.2s;
  outline-offset: 1px;

  &:focus {
    ${interactiveFocusStyle}
  }

  &.is-selected {
    background-color: ${props => props.theme.palette.brand.primary.normal};
    box-shadow: ${props => props.theme.shadow.flat.small};
  }
`

interface Props {
  offer: App.HotelOffer;
  rooms: Array<App.RoomOccupants>;
  isFlightsSelected: boolean;
  isMultiRoomConnected: boolean;
  multipleRoomPackages: PackageOptionViewByRoom;
  multiRoomPackageTotals: MultiRoomPackageTotals;
  mobileMultiRoomMode: MobileMultiRoomMode;
  packageSelectionChanged: boolean;
  activeFilter?: OfferPageFilterType;
  scrollToSelections?: boolean;
}

function MobileMultipleRoomsBookingContent(props: Props) {
  const {
    offer,
    rooms,
    isFlightsSelected,
    isMultiRoomConnected,
    multipleRoomPackages,
    multiRoomPackageTotals,
    mobileMultiRoomMode = 'individual',
    packageSelectionChanged,
    activeFilter,
    scrollToSelections,
  } = props

  const dispatch = useOfferPageDispatch()
  const headerRef = useRef<HTMLDivElement | null>(null)

  const isFlightsAvailable = useMemo(() => Object.values(multipleRoomPackages).every(pkg => pkg?.flightsAvailable), [multipleRoomPackages])
  const { hotelPlusFlightsPrice, hotelPlusFlightsMemberPrice, flightPrice } = multiRoomPackageTotals
  const displayBlundledFlightPrice = hotelPlusFlightsMemberPrice ?? hotelPlusFlightsPrice
  const hasFlights = !!flightPrice && !offer.flightCacheDisabled

  const onSelectIndividual = useCallback(() => {
    dispatch({ type: OfferPageActions.toggleMobileMultiRoomMode, mobileMultiRoomMode: 'individual' })
  }, [dispatch])

  const onSelectSameForAllRooms = useCallback(() => {
    dispatch({ type: OfferPageActions.toggleMobileMultiRoomMode, mobileMultiRoomMode: 'same-room' })
  }, [dispatch])

  const onToggleConnectedRooms = useCallback(() => {
    dispatch({ type: OfferPageActions.toggleConnectedRooms })
  }, [dispatch])

  const onSelectHotelFlights = useCallback(() => {
    if (!isFlightsSelected) {
      showSnackbar('Prices now include flights. You\'ll be able to select flights on the next step', 'success', { action: { label: 'OK' } })
    }
    dispatch({ type: OfferPageActions.toggleFlightsPrice, isFlightsSelected: !isFlightsSelected })
  }, [isFlightsSelected, dispatch])

  const showPreSelectionBanner = !activeFilter && mobileMultiRoomMode === 'individual' && !packageSelectionChanged
  const isConfirmStep = activeFilter === 'multiRoomConfirm' || activeFilter === 'multiRoomSelect'

  return (
    <Pane background="neutral-six">
      <Group direction="vertical" gap={8}>
        <HeaderSection ref={headerRef}>
          <Group direction="vertical" gap={16}>
            {!isConfirmStep && <Heading variant="heading4">Select your {pluralizeToString('room', rooms.length)}</Heading>}
            {isConfirmStep && packageSelectionChanged && <MessageBanner kind="warning" description="Your previous room selection is no longer available, please review your new selection."/>}
            <Group direction="vertical" gap={12}>
              {!isConfirmStep && <BodyText variant="medium">Choose the same package for all rooms, or customise each room individually.</BodyText>}
              <ToggleButtonsContainer direction="horizontal" gap={4}>
                <ToggleButton className={cn({ 'is-selected': mobileMultiRoomMode === 'individual' })} onClick={onSelectIndividual}>
                  <BodyText
                    variant="medium"
                    weight={mobileMultiRoomMode === 'individual' ? 'bold' : undefined}
                    colour={mobileMultiRoomMode === 'individual' ? 'neutral-eight' : 'neutral-one'}
                  >
                    Individually
                  </BodyText>
                </ToggleButton>
                <ToggleButton className={cn({ 'is-selected': mobileMultiRoomMode === 'same-room' })} onClick={onSelectSameForAllRooms}>
                  <BodyText
                    variant="medium"
                    weight={mobileMultiRoomMode === 'same-room' ? 'bold' : undefined}
                    colour={mobileMultiRoomMode === 'same-room' ? 'neutral-eight' : 'neutral-one'}
                  >
                    Same for all rooms
                  </BodyText>
                </ToggleButton>
              </ToggleButtonsContainer>
            </Group>
            {showPreSelectionBanner && <Sheet background="highlight-primary" padding="8 20">
              <Caption variant="medium">We've pre-selected the best-value rooms to get you started. Customise each room below.</Caption>
            </Sheet>}
          </Group>
        </HeaderSection>
        <TogglesSection>
          <ClickableListItem
            title="Request connecting rooms"
            subtitle="Subject to availability. Confirmed at check-in."
            startIcon={<LineDoorIcon />}
          >
            <Switch checked={isMultiRoomConnected} onChange={onToggleConnectedRooms} />
          </ClickableListItem>
          {isFlightsAvailable && (<>
            <DividerContainer><Divider kind="primary" /></DividerContainer>
            <ClickableListItem
              title="Add flights for all travellers"
              subtitle={displayBlundledFlightPrice ? <>From <FormatCurrency value={displayBlundledFlightPrice} /> total{!hasFlights && '*'}</> : undefined}
              startIcon={<LinePlaneDepartureIcon />}
            >
              <Switch checked={isFlightsSelected} onChange={onSelectHotelFlights} />
            </ClickableListItem>
          </>)}
        </TogglesSection>
        <Pane>
          {mobileMultiRoomMode === 'individual' && <MobileMultipleRoomsIndividualRoomsContent />}
          {mobileMultiRoomMode === 'same-room' && <MobileMultipleRoomsSameRoomContent showSelection={!!scrollToSelections && isConfirmStep} headerRef={headerRef} />}
        </Pane>
      </Group>
    </Pane>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  offer: state.offer,
  rooms: state.rooms,
  isFlightsSelected: state.flights,
  isMultiRoomConnected: state.isMultiRoomConnected,
  multipleRoomPackages: getActivePackagesByRoom(state, appState),
  multiRoomPackageTotals: getMultiRoomPackageTotals(state, appState),
  packageSelectionChanged: getHasPackageChangedFromSelection(state, appState),
  mobileMultiRoomMode: state.mobileMultiRoomMode,
  activeFilter: state.activeFilter,
}), MobileMultipleRoomsBookingContent)
